import { useEffect, useState } from 'react';

/**
 * Theme preference: system (default) → light → dark, persisted. The CSS in
 * theme.css honors both the media query and the data-theme override, so "system"
 * simply removes the attribute.
 */
export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'sw-theme';

export function applyStoredTheme(): void {
  const pref = (localStorage.getItem(KEY) as ThemePref | null) ?? 'system';
  if (pref === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', pref);
}

export function useTheme(): [ThemePref, () => void] {
  const [pref, setPref] = useState<ThemePref>(
    () => (localStorage.getItem(KEY) as ThemePref | null) ?? 'system',
  );
  useEffect(() => {
    if (pref === 'system') {
      localStorage.removeItem(KEY);
      document.documentElement.removeAttribute('data-theme');
    } else {
      localStorage.setItem(KEY, pref);
      document.documentElement.setAttribute('data-theme', pref);
    }
  }, [pref]);
  const cycle = () =>
    setPref((p) => (p === 'system' ? 'light' : p === 'light' ? 'dark' : 'system'));
  return [pref, cycle];
}
